<script lang="ts">
import { defineComponent, onActivated, onMounted, ref, watch, computed, nextTick } from 'vue'
import { useWorkers, type Worker } from '@/composables/useWorkers'
import { useJobs } from '@/composables/useJobs'
import { useAssignments } from '@/composables/useAssignments'
import { useEventLog } from '@/composables/useEventLog'
import { useAdmin } from '@/composables/useAdmin'
import { useDrawer } from '@/composables/useDrawer'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import { useProjects } from '@/composables/useProjects'
import { useAuthStore } from '@/stores/auth'

import { useRouter } from 'vue-router'
import ActiveProcessing from '@/components/ActiveProcessing.vue'
import WorkflowPipelineView from '@/views/WorkflowPipelineView.vue'

interface EventLog {
  id: number;
  timestamp: string;
  model_name: string;
  action: string;
  instance_id: number;
  details: string;
  user: number;
  user_username?: string;
  ip_address?: string;
  request_data?: any;
  [key: string]: any; // Add index signature to allow string indexing
}

type WorkerSortKey = 'id' | 'machine_id' | 'last_seen' | 'completed_jobs' | 'avg_timing' | 'capabilities'
type WorkerSortDirection = 'asc' | 'desc'

export default defineComponent({
  props: {
    active: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    ActiveProcessing,
    WorkflowPipelineView
  },
  setup(props) {
    // Call the composables to get items
    const { workers, fetchItems: fetchWorkers, workerKeys } = useWorkers()
    const { jobs, fetchJobs, jobKeys } = useJobs()
    const { assignments, fetchAssignments, assignmentKeys } = useAssignments()
    const { eventLogs, fetchEventLog, eventLogKeys, totalCount, currentPage, pageSize, loading, hasNext, hasPrevious, totalPages, nextPage, previousPage, goToPage, changePageSize } = useEventLog()
    const { cleanupSystem, clearEventLog, createTestWorker, updateTestWorker, deleteTestWorker, error, success } = useAdmin()

    const router = useRouter()
    const route = useRoute()
    const authStore = useAuthStore()
    const activeTab = ref('0')

    const parseTabFromQuery = (rawTab: unknown) => {
      const tabValue = Array.isArray(rawTab) ? rawTab[0] : rawTab
      if (tabValue == null) {
        return '0'
      }

      const normalized = String(tabValue).toLowerCase()
      if (normalized === 'jobs' || normalized === 'queued' || normalized === '1') {
        return '1'
      }
      if (['0', '2', '3', '4', '5', '6'].includes(normalized)) {
        return normalized
      }
      return '0'
    }

    const applyTabFromRoute = (rawTab: unknown) => {
      activeTab.value = parseTabFromQuery(rawTab)
    }
    
    // Track which details are expanded
    const expandedDetails = ref<Record<number, boolean>>({})

    const helpDrawer = useDrawer()
    const projectsInterface = useProjects()
    
    const hasTestWorker = computed(() => {
      return workers.value.some(w => w.machine_id === 'test-worker-dummy')
    })

    const workerCount = computed(() => workers.value.length)
    const jobCount = computed(() => jobs.value.length)
    const assignmentCount = computed(() => assignments.value.length)

    const assignedWorkerCount = computed(() => {
      const workerIds = new Set(
        workers.value
          .map((worker) => Number(worker.id))
          .filter((id) => Number.isFinite(id))
      )

      const assignedIds = new Set<number>()
      assignments.value.forEach((assignment: any) => {
        const workerId = Number(assignment?.worker)
        if (Number.isFinite(workerId) && workerIds.has(workerId)) {
          assignedIds.add(workerId)
        }
      })

      return assignedIds.size
    })

    const workerUtilization = computed(() => {
      if (workerCount.value === 0) {
        return 0
      }
      return Math.min(1, assignedWorkerCount.value / workerCount.value)
    })

    const workerUtilizationPercent = computed(() => Math.round(workerUtilization.value * 100))

    const workerUtilizationColor = computed(() => {
      const hue = Math.round(workerUtilization.value * 120)
      return `hsl(${hue}, 78%, 42%)`
    })
    
    helpDrawer.header = 'Processing Help'
    helpDrawer.content = [
      "This page shows the current state of the processing system.",
      "The 'Projects' tab shows the projects that are currently in progress.",
      "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
      "The 'Workers' table shows the workers that are connected to the system.",
      "The 'Assigned jobs' table shows the jobs that are currently being processed.",
      "The 'Event log' table shows the events that have occurred in the system.",
      "The 'Admin' tab provides system cleanup options for administrators.",
    ]

    // Fetch items when the component is mounted
    onMounted( async () => {
      applyTabFromRoute(route.query.tab)
      await fetchWorkers()
      await hydrateWorkerUserNames()
      fetchJobs()
      fetchAssignments()
      fetchEventLog()
      await projectsInterface.fetchProjects().then(() => {
        console.log("Projects:", projectsInterface.projects.value);
      });
      console.log('ProcessingView mounted')
    })

    watch(workers, () => {
      void hydrateWorkerUserNames()
    }, { deep: false })

    onActivated(() => {
      console.log('ProcessingView help drawer')
      helpDrawer.header = 'Processing Help'
      helpDrawer.content = [
        "This page shows the current state of the processing system.",
        "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
        "The 'Workers' table shows the workers that are connected to the system.",
        "The 'Assigned jobs' table shows the jobs that are currently being processed.",
        "The 'Event log' table shows the events that have occurred in the system.",
        "The 'Admin' tab provides system cleanup options for administrators.",
      ]
    })

    watch(() => props.active, (newVal) => {
      console.log('ProcessingView active', newVal)
      if (newVal) {
        helpDrawer.header = 'Processing Help'
        helpDrawer.content = [
          "This page shows the current state of the processing system.",
          "The 'Jobs queued' table shows the jobs that are waiting to be processed.",
          "The 'Workers' table shows the workers that are connected to the system.",
          "The 'Assigned jobs' table shows the jobs that are currently being processed.",
          "The 'Event log' table shows the events that have occurred in the system.",
          "The 'Admin' tab provides system cleanup options for administrators.",
        ]
      }
    })

    onBeforeRouteUpdate((to) => {
      applyTabFromRoute(to.query.tab)
    })

    // Manually define the columns and their widths for each table
    const tableConfig = {
      projects: {
        headers: ['id', 'author_username', 'name', 'area_of_interest_name', 'start_date', 'end_date', 'status'],
        columnWidths: ['8%', '15%', '20%', '15%', '14%', '14%', '14%'],
      },
      jobs: {
        headers: ['id', 'name', 'project', 'worker', 'args'],
        columnWidths: ['16%', '16%', '16%', '16%', '24%', '12%'],
      },
      workers: {
        columnWidths: ['7%', '22%', '18%', '18%', '9%', '8%', '10%', '8%'],
      },
      assignments: {
        headers: ['id', 'job', 'worker', 'completed'],
        columnWidths: ['22%', '22%', '22%', '22%', '12%'],
      },
      eventLog: {
        headers: ['timestamp', 'model_name', 'action', 'user_username', 'ip_address', 'details'],
        columnWidths: ['15%', '10%', '8%', '12%', '10%', '45%'],
      }
    }

    const formatHeaderKey = (key: string) => {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    }

    const getWorkerById = (workerId: unknown) => {
      const id = Number(workerId)
      if (!Number.isFinite(id)) {
        return null
      }
      return workers.value.find((w) => Number(w.id) === id) ?? null
    }

    const getProjectById = (projectId: unknown) => {
      const id = Number(projectId)
      if (!Number.isFinite(id)) {
        return null
      }
      return projectsInterface.projects.value.find((p) => Number(p.id) === id) ?? null
    }

    const getWorkerDisplay = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      if (!worker) {
        return String(workerId ?? '')
      }
      return worker.machine_id || String(worker.id ?? workerId ?? '')
    }

    const getProjectDisplay = (projectId: unknown) => {
      const project = getProjectById(projectId)
      if (!project) {
        return String(projectId ?? '')
      }
      return project.name || String(project.id ?? projectId ?? '')
    }

    const getHeartbeatAgeMs = (updated: unknown) => {
      if (typeof updated !== 'string' || !updated) {
        return Number.POSITIVE_INFINITY
      }
      const timestamp = new Date(updated).getTime()
      if (Number.isNaN(timestamp)) {
        return Number.POSITIVE_INFINITY
      }
      return Date.now() - timestamp
    }

    const getWorkerLastSeenTimestamp = (worker: Worker | null | undefined) => {
      if (!worker) {
        return ''
      }
      return typeof worker.last_seen === 'string' && worker.last_seen
        ? worker.last_seen
        : typeof worker.updated === 'string' && worker.updated
          ? worker.updated
          : ''
    }

    const getWorkerLastSeenDate = (worker: Worker | null | undefined) => {
      const lastSeen = getWorkerLastSeenTimestamp(worker)
      if (!lastSeen) {
        return null
      }
      const date = new Date(lastSeen)
      return Number.isNaN(date.getTime()) ? null : date
    }

    const getWorkerHeartbeatClass = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      const ageMs = getHeartbeatAgeMs(getWorkerLastSeenTimestamp(worker))

      if (ageMs <= 60_000) {
        return 'heartbeat-fresh'
      }
      if (ageMs > 300_000) {
        return 'heartbeat-stale'
      }
      if (ageMs > 150_000) {
        return 'heartbeat-warning'
      }
      return ''
    }

    const getWorkerHeartbeatClassFromWorker = (worker: Worker) => {
      const ageMs = getHeartbeatAgeMs(getWorkerLastSeenTimestamp(worker))

      if (ageMs <= 60_000) {
        return 'heartbeat-fresh'
      }
      if (ageMs > 300_000) {
        return 'heartbeat-stale'
      }
      if (ageMs > 150_000) {
        return 'heartbeat-warning'
      }
      return ''
    }

    const formatDateTime = (value: unknown) => {
      if (typeof value !== 'string' || !value) {
        return 'unknown'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return 'unknown'
      }
      return date.toLocaleString()
    }

    const getWorkerCompletedJobs = (worker: Worker) => {
      const completedJobs = worker.notes?.completed_jobs
      return Array.isArray(completedJobs) ? completedJobs : []
    }

    const getWorkerCompletedJobCount = (worker: Worker) => getWorkerCompletedJobs(worker).length

    const getWorkerAverageTimingSeconds = (worker: Worker) => {
      const timings = getWorkerCompletedJobs(worker)
        .map((job) => Number(job?.timing))
        .filter((timing) => Number.isFinite(timing))

      if (timings.length === 0) {
        return null
      }

      const total = timings.reduce((sum, value) => sum + value, 0)
      return total / timings.length
    }

    const formatTiming = (seconds: number | null) => {
      if (seconds == null || !Number.isFinite(seconds)) {
        return 'n/a'
      }
      return `${seconds.toFixed(1)}s`
    }

    const getWorkerCapabilities = (worker: Worker) => {
      const capabilities = worker.notes?.capabilities
      if (!Array.isArray(capabilities)) {
        return [] as string[]
      }
      return capabilities
        .map((capability) => String(capability).trim())
        .filter((capability) => capability.length > 0)
    }

    const formatCapabilities = (worker: Worker) => {
      const capabilities = getWorkerCapabilities(worker)
      return capabilities.length > 0 ? capabilities.join(', ') : 'none'
    }

    const userNameById = ref<Record<number, string>>({})
    const usersInFlight = new Set<number>()

    const fetchUserName = async (userId: unknown) => {
      const id = Number(userId)
      if (!Number.isFinite(id) || id <= 0) {
        return
      }
      if (userNameById.value[id] || usersInFlight.has(id)) {
        return
      }

      usersInFlight.add(id)
      try {
        const response = await fetch(`/api/users/${id}/`)
        if (!response.ok) {
          return
        }
        const payload = await response.json()
        const username = typeof payload?.username === 'string' ? payload.username : ''
        if (username) {
          userNameById.value = {
            ...userNameById.value,
            [id]: username
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        usersInFlight.delete(id)
      }
    }

    const hydrateWorkerUserNames = async () => {
      const uniqueIds = [...new Set(
        workers.value
          .map((worker) => Number(worker.user))
          .filter((id) => Number.isFinite(id) && id > 0)
      )]
      await Promise.all(uniqueIds.map((id) => fetchUserName(id)))
    }

    const formatCapabilityLabel = (capability: string) => {
      const normalized = capability.trim().toLowerCase()
      if (normalized === 'download') {
        return 'Download'
      }

      if (normalized.startsWith('memory_gb:')) {
        const value = capability.split(':')[1]
        const numeric = Number(value)
        if (Number.isFinite(numeric)) {
          return `${numeric} GB RAM`
        }
      }

      return capability
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    }

    const getWorkerUserDisplay = (worker: Worker) => {
      const id = Number(worker.user)
      if (!Number.isFinite(id)) {
        return 'unknown'
      }
      return userNameById.value[id] ?? `#${id}`
    }

    const getCompletedJobsTooltip = (worker: Worker) => {
      const completedJobs = getWorkerCompletedJobs(worker)
      if (completedJobs.length === 0) {
        return 'No completed jobs recorded'
      }

      return completedJobs
        .slice(-12)
        .reverse()
        .map((job, index) => {
          const name = job?.name ?? 'Unknown'
          const status = job?.status ?? 'unknown'
          const timing = Number(job?.timing)
          const timingLabel = Number.isFinite(timing) ? `${timing.toFixed(1)}s` : 'n/a'
          const completedAt = typeof job?.completed_at === 'string' && job.completed_at
            ? formatDateTime(job.completed_at)
            : 'unknown time'
          return `${index + 1}. ${name} [${status}] - ${timingLabel} - ${completedAt}`
        })
        .join('\n')
    }

    const recentCompletedJobs = computed(() => {
      const entries: Array<{
        workerMachineId: string
        workerUser: string
        name: string
        status: string
        timing: number
        completedAt: string
        completedAtMs: number
      }> = []

      workers.value.forEach((worker) => {
        const completedJobs = getWorkerCompletedJobs(worker)
        completedJobs.forEach((job) => {
          const completedAt = typeof job?.completed_at === 'string' ? job.completed_at : ''
          const completedAtMs = completedAt ? new Date(completedAt).getTime() : 0

          entries.push({
            workerMachineId: worker.machine_id,
            workerUser: getWorkerUserDisplay(worker),
            name: job?.name ?? 'Unknown',
            status: job?.status ?? 'unknown',
            timing: Number(job?.timing),
            completedAt,
            completedAtMs: Number.isFinite(completedAtMs) ? completedAtMs : 0
          })
        })
      })

      return entries.sort((left, right) => right.completedAtMs - left.completedAtMs)
    })

    const recentCompletionsCount = computed(() => recentCompletedJobs.value.length)

    const recentCompletionsLastAtLabel = computed(() => {
      const latest = recentCompletedJobs.value[0]
      if (!latest || !latest.completedAt) {
        return 'No completions yet'
      }
      return `Latest: ${formatDateTime(latest.completedAt)}`
    })

    const recentCompletionsTooltip = computed(() => {
      const items = recentCompletedJobs.value.slice(0, 8)
      if (items.length === 0) {
        return 'No recent job completions'
      }

      return items
        .map((item, index) => {
          const timingLabel = Number.isFinite(item.timing) ? `${item.timing.toFixed(1)}s` : 'n/a'
          const completedAtLabel = item.completedAt ? formatDateTime(item.completedAt) : 'unknown time'
          return `${index + 1}. ${item.name} [${item.status}] - ${timingLabel}\n   ${item.workerMachineId} (User: ${item.workerUser})\n   ${completedAtLabel}`
        })
        .join('\n')
    })

    const getAssignmentByWorkerId = (workerId: unknown) => {
      const id = Number(workerId)
      if (!Number.isFinite(id)) {
        return null
      }

      return assignments.value.find((assignment: any) => Number(assignment?.worker) === id) ?? null
    }

    const hasWorkerAssignment = (worker: Worker) => {
      return getAssignmentByWorkerId(worker.id) != null
    }

    const goToWorkerAssignment = async (worker: Worker) => {
      const assignment = getAssignmentByWorkerId(worker.id)
      if (!assignment?.id) {
        return
      }

      activeTab.value = '3'
      await nextTick()
      const target = document.getElementById(`assignment-${assignment.id}`)
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    const workersColumnCount = 8
    const workerSortKey = ref<WorkerSortKey>('last_seen')
    const workerSortDirection = ref<WorkerSortDirection>('desc')

    const sortWorkers = (left: Worker, right: Worker, key: WorkerSortKey) => {
      if (key === 'id') {
        return Number(left.id ?? 0) - Number(right.id ?? 0)
      }
      if (key === 'machine_id') {
        return String(left.machine_id ?? '').localeCompare(String(right.machine_id ?? ''))
      }
      if (key === 'last_seen') {
        const leftTime = getWorkerLastSeenDate(left)?.getTime() ?? 0
        const rightTime = getWorkerLastSeenDate(right)?.getTime() ?? 0
        return leftTime - rightTime
      }
      if (key === 'completed_jobs') {
        return getWorkerCompletedJobCount(left) - getWorkerCompletedJobCount(right)
      }
      if (key === 'avg_timing') {
        const leftAvg = getWorkerAverageTimingSeconds(left)
        const rightAvg = getWorkerAverageTimingSeconds(right)
        const leftSortable = leftAvg == null ? Number.POSITIVE_INFINITY : leftAvg
        const rightSortable = rightAvg == null ? Number.POSITIVE_INFINITY : rightAvg
        return leftSortable - rightSortable
      }

      const leftCapabilities = getWorkerCapabilities(left).length
      const rightCapabilities = getWorkerCapabilities(right).length
      return leftCapabilities - rightCapabilities
    }

    const sortedWorkers = computed(() => {
      const items = [...workers.value]
      const key = workerSortKey.value
      const direction = workerSortDirection.value === 'asc' ? 1 : -1
      return items.sort((left, right) => sortWorkers(left, right, key) * direction)
    })

    const setWorkerSort = (key: WorkerSortKey) => {
      if (workerSortKey.value === key) {
        workerSortDirection.value = workerSortDirection.value === 'asc' ? 'desc' : 'asc'
        return
      }
      workerSortKey.value = key
      workerSortDirection.value = key === 'machine_id' ? 'asc' : 'desc'
    }

    const getWorkerSortIndicator = (key: WorkerSortKey) => {
      if (workerSortKey.value !== key) {
        return '↕'
      }
      return workerSortDirection.value === 'asc' ? '▲' : '▼'
    }

    const formatHeartbeatAge = (updated: unknown) => {
      const ageMs = getHeartbeatAgeMs(updated)
      if (!Number.isFinite(ageMs)) {
        return 'unknown'
      }

      const minutes = Math.floor(ageMs / 60_000)
      const seconds = Math.floor((ageMs % 60_000) / 1000)
      if (minutes <= 0) {
        return `${seconds}s ago`
      }
      return `${minutes}m ${seconds}s ago`
    }

    const getWorkerTooltip = (workerId: unknown) => {
      const worker = getWorkerById(workerId)
      if (!worker) {
        return String(workerId ?? '')
      }
      return `${worker.machine_id}\nLast heartbeat: ${formatHeartbeatAge(getWorkerLastSeenTimestamp(worker))}`
    }

    const formatArgs = (args: string) => {
      if (!args) return ''
      const newArgs = args.split(',')
      const formattedArgs: { key: string; value: string }[] = []
      for (let i = 0; i < newArgs.length; i += 2) {
        if (i + 1 < newArgs.length) {
          formattedArgs.push({ key: newArgs[i], value: newArgs[i + 1] })
        }
      }
      const annoyingKeys = ['DesiredOutput']
      const filteredArgs = formattedArgs.filter(arg => !annoyingKeys.includes(arg.key))
      return filteredArgs.map(arg => `${arg.key}: ${arg.value}`).join('; ')
    }

    const formatDetails = (details: string, logId: number) => {
      try {
        // Parse the JSON string
        const detailsObj = JSON.parse(details)
        
        // Format with indentation for better readability
        const formattedJson = JSON.stringify(detailsObj, null, 2)
        const lines = formattedJson.split('\n')
        
        // If not expanded and has more than 10 lines, truncate
        if (!expandedDetails.value[logId] && lines.length > 10) {
          return lines.slice(0, 10).join('\n') + '\n...'
        }
        
        return formattedJson
      } catch (e) {
        // Return original if not valid JSON
        return details
      }
    }

    const isDetailsTruncated = (details: string) => {
      try {
        const detailsObj = JSON.parse(details)
        const formattedJson = JSON.stringify(detailsObj, null, 2)
        return formattedJson.split('\n').length > 10
      } catch (e) {
        return false
      }
    }

    const toggleDetails = (logId: number) => {
      expandedDetails.value[logId] = !expandedDetails.value[logId]
    }

    const handleCleanupSystem = async () => {
      await cleanupSystem()
      // Refresh all data after cleanup
      fetchWorkers()
      fetchJobs()
      fetchAssignments()
      fetchEventLog(undefined, undefined, { force: true })
    }

    const handleClearEventLog = async () => {
      await clearEventLog()
      fetchEventLog(undefined, undefined, { force: true })
    }

    const ensureProjectsLoaded = async () => {
      if (projectsInterface.projects.value.length === 0) {
        await projectsInterface.fetchProjects()
      }
    }

    const refreshActiveTab = async (tab: string, options: { forceEventLog?: boolean } = {}) => {
      if (tab === '0') {
        await projectsInterface.fetchProjects()
        return
      }

      if (tab === '1') {
        await Promise.all([
          fetchJobs(),
          fetchWorkers(),
          ensureProjectsLoaded()
        ])
        await hydrateWorkerUserNames()
        return
      }

      if (tab === '2') {
        await fetchWorkers()
        await hydrateWorkerUserNames()
        return
      }

      if (tab === '3') {
        await Promise.all([
          fetchAssignments(),
          fetchWorkers()
        ])
        await hydrateWorkerUserNames()
        return
      }

      if (tab === '4') {
        await fetchEventLog(undefined, undefined, { force: options.forceEventLog === true })
      }
    }

    const handleRefreshAll = async (options: { forceEventLog?: boolean } = {}) => {
      console.log('Refreshing all tables...')
      await Promise.all([
        fetchWorkers(),
        fetchJobs(),
        fetchAssignments(),
        fetchEventLog(undefined, undefined, { force: options.forceEventLog === true }),
        projectsInterface.fetchProjects()
      ])
      await hydrateWorkerUserNames()
      console.log('All tables refreshed')
    }

    const handleManualRefreshAll = async () => {
      await handleRefreshAll({ forceEventLog: true })
    }

    const handleTabChange = async (value: string | number) => {
      activeTab.value = String(value)
      await refreshActiveTab(activeTab.value)

      const nextQuery: Record<string, any> = { ...route.query }
      nextQuery.tab = activeTab.value === '1' ? 'jobs' : activeTab.value
      void router.replace({ query: nextQuery })
    }

    const handleCreateTestWorker = async () => {
      await createTestWorker()
      await fetchWorkers()
      await hydrateWorkerUserNames()
    }

    const handleUpdateTestWorker = async () => {
      const testWorker = workers.value.find(w => w.machine_id === 'test-worker-dummy')
      if (testWorker?.id) {
        await updateTestWorker(testWorker.id)
        await fetchWorkers()
        await hydrateWorkerUserNames()
      }
    }

    const handleDeleteTestWorker = async () => {
      const testWorker = workers.value.find(w => w.machine_id === 'test-worker-dummy')
      if (testWorker?.id) {
        await deleteTestWorker(testWorker.id)
        await fetchWorkers()
        await hydrateWorkerUserNames()
      }
    }

    const handlePageSizeChange = (event: Event) => {
      const target = event.target as HTMLSelectElement
      if (target) {
        changePageSize(Number(target.value))
      }
    }

    const deleteById = async (resource: 'jobs' | 'workers' | 'assignments', id: number) => {
      const csrfToken = await authStore.getCsrfToken()
      const response = await fetch(`/api/${resource}/${id}/`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken ?? ''
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to delete ${resource.slice(0, -1)} ${id}`)
      }
    }

    const handleDeleteJob = async (jobId?: number) => {
      if (jobId == null) {
        console.warn('Cannot delete job: missing id')
        return
      }

      if (!confirm(`Delete job ${jobId}? This cannot be undone.`)) {
        return
      }

      try {
        await deleteById('jobs', jobId)
        await fetchJobs()
      } catch (e) {
        console.error(e)
      }
    }

    const handleDeleteWorker = async (workerId?: number) => {
      if (workerId == null) {
        console.warn('Cannot delete worker: missing id')
        return
      }

      if (!confirm(`Delete worker ${workerId}? This cannot be undone.`)) {
        return
      }

      try {
        await deleteById('workers', workerId)
        await fetchWorkers()
        await hydrateWorkerUserNames()
      } catch (e) {
        console.error(e)
      }
    }

    const handleDeleteAssignment = async (assignmentId?: number) => {
      if (assignmentId == null) {
        console.warn('Cannot delete assignment: missing id')
        return
      }

      if (!confirm(`Delete assignment ${assignmentId}? This cannot be undone.`)) {
        return
      }

      try {
        await deleteById('assignments', assignmentId)
        await fetchAssignments()
      } catch (e) {
        console.error(e)
      }
    }

    const goToAdminNotifications = () => {
      router.push('/admin-notifications')
    }

    const goToSchemesOfAccess = () => {
      router.push('/schemes-of-access')
    }

    const goToCommandControlPanel = () => {
      router.push('/admin-workflows')
    }

    return {
      workers,
      workerKeys,
      jobs,
      jobKeys,
      assignments,
      assignmentKeys,
      eventLogs,
      eventLogKeys,
      expandedDetails,
      formatHeaderKey,
      formatArgs,
      formatDetails,
      toggleDetails,
      tableConfig,
      helpDrawer,
      activeTab,
      projectsInterface,
      handleCleanupSystem,
      handleClearEventLog,
      handleRefreshAll,
      handleManualRefreshAll,
      handleTabChange,
      handleCreateTestWorker,
      handleUpdateTestWorker,
      handleDeleteTestWorker,
      hasTestWorker,
      workerCount,
      jobCount,
      assignmentCount,
      assignedWorkerCount,
      workerUtilizationPercent,
      workerUtilizationColor,
      error,
      success,
      isDetailsTruncated,
      goToAdminNotifications,
      goToSchemesOfAccess,
      goToCommandControlPanel,
      totalCount,
      currentPage,
      pageSize,
      loading,
      hasNext,
      hasPrevious,
      totalPages,
      nextPage,
      previousPage,
      goToPage,
      changePageSize,
      handlePageSizeChange,
      getWorkerTooltip,
      getWorkerHeartbeatClass,
      getWorkerHeartbeatClassFromWorker,
      formatHeartbeatAge,
      getWorkerDisplay,
      getProjectDisplay,
      formatDateTime,
      getWorkerCompletedJobCount,
      getWorkerAverageTimingSeconds,
      formatTiming,
      formatCapabilities,
      formatCapabilityLabel,
      getWorkerCapabilities,
      getWorkerUserDisplay,
      getCompletedJobsTooltip,
      recentCompletionsCount,
      recentCompletionsLastAtLabel,
      recentCompletionsTooltip,
      hasWorkerAssignment,
      getAssignmentByWorkerId,
      goToWorkerAssignment,
      sortedWorkers,
      setWorkerSort,
      getWorkerSortIndicator,
      workersColumnCount,
      handleDeleteJob,
      handleDeleteWorker,
      handleDeleteAssignment
    }
  }
})

</script>


<template>
  <h1>Processing</h1>
  <div class="header-actions">
    <Button @click="helpDrawer.visible=true">?</Button>
    <Button @click="handleManualRefreshAll" class="refresh-button">Refresh All</Button>
  </div>
  <div class="stats-strip">
    <div class="stat-card">
      <div class="stat-label">Workers</div>
      <div class="stat-value">{{ workerCount }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-label">Jobs</div>
      <div class="stat-value">{{ jobCount }}</div>
    </div>
    <div class="stat-card">
      <div class="stat-label">Assignments</div>
      <div class="stat-value">{{ assignmentCount }}</div>
    </div>
    <div class="stat-card stat-wide">
      <div class="utilization-header">
        <span class="stat-label">Worker Utilization</span>
        <span class="utilization-percent" :style="{ color: workerUtilizationColor }">{{ workerUtilizationPercent }}%</span>
      </div>
      <div class="utilization-bar">
        <div
          class="utilization-fill"
          :style="{ width: `${workerUtilizationPercent}%`, backgroundColor: workerUtilizationColor }"
        />
      </div>
      <div class="utilization-meta">
        {{ assignedWorkerCount }} / {{ workerCount }} workers assigned
      </div>
    </div>
    <div class="stat-card tooltip" :data-fulltext="recentCompletionsTooltip">
      <div class="stat-label">Recent Job Completions</div>
      <div class="stat-value">{{ recentCompletionsCount }}</div>
      <div class="utilization-meta">{{ recentCompletionsLastAtLabel }}</div>
    </div>
  </div>
  <Tabs :value="activeTab" @update:value="handleTabChange">
    <TabList>
      <Tab value="0">Active Projects</Tab>
      <Tab value="1">Jobs queued</Tab>
      <Tab value="2">Workers</Tab>
      <Tab value="3">Assigned Jobs</Tab>
      <Tab value="4">Event Log</Tab>
      <Tab value="5">Admin</Tab>
      <Tab value="6">Pipeline Overview</Tab>
    </TabList>  
    <TabPanels>
      <TabPanel value="0">
        <ActiveProcessing />
      </TabPanel>
      <TabPanel value="1">
        <h2>Jobs queued</h2>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.jobs.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.jobs.headers" :key="index">{{ formatHeaderKey(header) }}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="job in jobs" :key="job.id">
              <td
                v-for="(header, index) in tableConfig.jobs.headers"
                :key="index"
                :data-fulltext="header === 'worker' ? getWorkerTooltip(job[header]) : header === 'project' ? getProjectDisplay(job[header]) : job[header]"
                class="tooltip"
              >
                <template v-if="header === 'args'">
                  {{ formatArgs(job[header]) }}
                </template>
                <template v-else-if="header === 'worker'">
                  <span :class="['worker-value', getWorkerHeartbeatClass(job[header])]">
                    {{ getWorkerDisplay(job[header]) }}
                  </span>
                </template>
                <template v-else-if="header === 'project'">
                  {{ getProjectDisplay(job[header]) }}
                </template>
                <template v-else-if="header === 'id'">
                  <a
                    class="raw-id-link"
                    :href="`/api/jobs/${job.id}/`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ job[header] }}
                  </a>
                </template>
                <template v-else>
                  {{ job[header] }}
                </template>
              </td>
              <td>
                <Button class="action-delete" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteJob(job.id)">
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="jobs.length === 0">
              <td :colspan="tableConfig.jobs.headers.length + 1">No jobs queued.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="2">
        <h2>Workers</h2>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.workers.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('id')">
                  ID <span class="sort-indicator">{{ getWorkerSortIndicator('id') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('machine_id')">
                  Worker <span class="sort-indicator">{{ getWorkerSortIndicator('machine_id') }}</span>
                </button>
              </th>
              <th>Software Version</th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('last_seen')">
                  Heartbeat <span class="sort-indicator">{{ getWorkerSortIndicator('last_seen') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('completed_jobs')">
                  Completed Jobs <span class="sort-indicator">{{ getWorkerSortIndicator('completed_jobs') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('avg_timing')">
                  Avg Timing <span class="sort-indicator">{{ getWorkerSortIndicator('avg_timing') }}</span>
                </button>
              </th>
              <th>
                <button type="button" class="sort-button" @click="setWorkerSort('capabilities')">
                  Capabilities <span class="sort-indicator">{{ getWorkerSortIndicator('capabilities') }}</span>
                </button>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in sortedWorkers" :key="item.id">
              <td :data-fulltext="String(item.id ?? '')" class="tooltip">
                <a
                  class="raw-id-link"
                  :href="`/api/workers/${item.id}/`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ item.id }}
                </a>
              </td>
              <td :data-fulltext="`${item.machine_id}\nUser: ${getWorkerUserDisplay(item)}`" class="tooltip">
                <div class="worker-primary">{{ item.machine_id }}</div>
                <div class="worker-secondary">User: {{ getWorkerUserDisplay(item) }}</div>
              </td>
              <td :data-fulltext="item.processing_software_version" class="tooltip">
                <span class="worker-version">{{ item.processing_software_version }}</span>
              </td>
              <td :data-fulltext="`Last seen: ${formatDateTime(item.last_seen || item.updated)}\nAge: ${formatHeartbeatAge(item.last_seen || item.updated)}`" class="tooltip">
                <div :class="['worker-primary', getWorkerHeartbeatClassFromWorker(item)]">
                  {{ formatHeartbeatAge(item.last_seen || item.updated) }}
                </div>
                <div class="worker-secondary">{{ formatDateTime(item.last_seen || item.updated) }}</div>
                <button
                  v-if="hasWorkerAssignment(item)"
                  type="button"
                  class="assignment-link"
                  @click="goToWorkerAssignment(item)"
                >
                  ● Assigned #{{ getAssignmentByWorkerId(item.id)?.id }}
                </button>
              </td>
              <td :data-fulltext="getCompletedJobsTooltip(item)" class="tooltip">
                {{ getWorkerCompletedJobCount(item) }}
              </td>
              <td :data-fulltext="`Average timing: ${formatTiming(getWorkerAverageTimingSeconds(item))}`" class="tooltip">
                {{ formatTiming(getWorkerAverageTimingSeconds(item)) }}
              </td>
              <td :data-fulltext="formatCapabilities(item)" class="tooltip">
                <div class="capability-list">
                  <span
                    v-for="capability in getWorkerCapabilities(item)"
                    :key="`${item.id}-${capability}`"
                    class="capability-chip"
                  >
                    {{ formatCapabilityLabel(capability) }}
                  </span>
                  <span v-if="getWorkerCapabilities(item).length === 0" class="worker-secondary">none</span>
                </div>
              </td>
              <td class="actions-cell">
                <Button class="action-delete worker-delete-button" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteWorker(item.id)">
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="sortedWorkers.length === 0">
              <td :colspan="workersColumnCount">No workers connected.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="3">
        <h2>Assigned Jobs</h2>
        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.assignments.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.assignments.headers" :key="index">{{ formatHeaderKey(header) }}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="assignment in assignments" :key="assignment.id" :id="`assignment-${assignment.id}`">
              <td
                v-for="(header, index) in tableConfig.assignments.headers"
                :key="index"
                :data-fulltext="header === 'worker' ? getWorkerTooltip(assignment[header]) : assignment[header]"
                class="tooltip"
              >
                <template v-if="header === 'worker'">
                  <span :class="['worker-value', getWorkerHeartbeatClass(assignment[header])]">
                    {{ getWorkerDisplay(assignment[header]) }}
                  </span>
                </template>
                <template v-else-if="header === 'id'">
                  <a
                    class="raw-id-link"
                    :href="`/api/assignments/${assignment.id}/`"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ assignment[header] }}
                  </a>
                </template>
                <template v-else>
                  {{ assignment[header] }}
                </template>
              </td>
              <td>
                <Button class="action-delete" severity="danger" text size="small" icon="pi pi-trash" @click="handleDeleteAssignment(assignment.id)">
                  Delete
                </Button>
              </td>
            </tr>
            <tr v-if="assignments.length === 0">
              <td :colspan="tableConfig.assignments.headers.length + 1">No assigned jobs.</td>
            </tr>
          </tbody>
        </table>
      </TabPanel>
      <TabPanel value="4">
        <h2>Event Log</h2>
        
        <!-- Pagination Controls - Top -->
        <div class="pagination-controls">
          <div class="pagination-info">
            <span v-if="loading">Loading...</span>
            <span v-else>
              Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} events
            </span>
          </div>
          <div class="pagination-actions">
            <label for="pageSize">Page Size:</label>
            <select id="pageSize" :value="pageSize" @change="handlePageSizeChange">
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            
            <Button @click="previousPage" :disabled="!hasPrevious || loading">Previous</Button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <Button @click="nextPage" :disabled="!hasNext || loading">Next</Button>
          </div>
        </div>

        <table>
          <colgroup>
            <col v-for="(width, index) in tableConfig.eventLog.columnWidths" :key="index" :style="{ width }" />
          </colgroup>
          <thead>
            <tr>
              <th v-for="(header, index) in tableConfig.eventLog.headers" :key="index">{{ formatHeaderKey(header) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in eventLogs" :key="log.id">
              <td v-for="(header, index) in tableConfig.eventLog.headers" :key="index" :data-fulltext="log[header]" class="tooltip">
                <template v-if="header === 'details'">
                  <div 
                    class="details-container" 
                    @click="toggleDetails(log.id)"
                    :class="{ 'truncated': isDetailsTruncated(log[header]) && !expandedDetails[log.id] }"
                  >
                    <pre class="details-json">{{ formatDetails(log[header], log.id) }}</pre>
                    <div v-if="isDetailsTruncated(log[header])" class="expand-indicator">
                      {{ expandedDetails[log.id] ? '▲ Collapse' : '▼ Expand' }}
                    </div>
                  </div>
                </template>
                <template v-else>
                  {{ log[header] }}
                </template>
              </td>
            </tr>
            <tr v-if="eventLogs.length === 0 && !loading">
              <td :colspan="tableConfig.eventLog.headers.length">No events logged.</td>
            </tr>
            <tr v-if="loading">
              <td :colspan="tableConfig.eventLog.headers.length">Loading...</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination Controls - Bottom -->
        <div class="pagination-controls">
          <div class="pagination-actions">
            <Button @click="previousPage" :disabled="!hasPrevious || loading">Previous</Button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <Button @click="nextPage" :disabled="!hasNext || loading">Next</Button>
          </div>
        </div>
      </TabPanel>
      <TabPanel value="5">
        <h2>Admin Panel</h2>
        <div class="admin-panel">
          <div class="admin-message" v-if="error" style="color: red">
            {{ error }}
          </div>
          <div class="admin-message" v-if="success" style="color: green">
            {{ success }}
          </div>
          <div class="admin-actions">
            <Button @click="handleCleanupSystem" class="danger-button">
              Delete All Jobs, Workers, and Assignments
            </Button>
            <Button @click="handleClearEventLog" class="danger-button">
              Clear Event Log
            </Button>
            <div class="test-worker-actions">
              <h3>Test Worker Actions</h3>
              <Button @click="handleCreateTestWorker" class="test-button">
                Create Test Worker
              </Button>
              <Button 
                @click="handleUpdateTestWorker" 
                class="test-button"
                :disabled="!hasTestWorker"
              >
                Update Test Worker
              </Button>
              <Button 
                @click="handleDeleteTestWorker" 
                class="test-button"
                :disabled="!hasTestWorker"
              >
                Delete Test Worker
              </Button>
              <div class="admin-notifications">
                <h3>Admin Notifications</h3>
                <a @click="goToAdminNotifications">Notifications</a>
              </div>
              <div class="schemes-of-access">
                <h3>Schemes of Access</h3>
                <a @click="goToSchemesOfAccess">Schemes of Access</a>
              </div>
              <div class="command-control-panel">
                <h3>Command Control Panel</h3>
                <a @click="goToCommandControlPanel">Workflow Management</a>
              </div>
            </div>

          </div>
        </div>
      </TabPanel>
      <TabPanel value="6">
        <WorkflowPipelineView />
      </TabPanel>
    </TabPanels>
  </Tabs>

</template>

<style scoped>
.wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.stats-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.stat-card {
  border: 1px solid #d9e3f5;
  border-radius: 8px;
  padding: 12px;
  background: #f8fbff;
}

.stat-wide {
  grid-column: span 2;
}

.stat-label {
  font-size: 12px;
  color: #4b5563;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.utilization-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.utilization-percent {
  font-size: 22px;
  font-weight: 700;
}

.utilization-bar {
  height: 10px;
  width: 100%;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.utilization-fill {
  height: 100%;
  transition: width 0.2s ease, background-color 0.2s ease;
}

.utilization-meta {
  margin-top: 8px;
  font-size: 12px;
  color: #4b5563;
}

@media (max-width: 900px) {
  .stat-wide {
    grid-column: span 1;
  }
}

.refresh-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.refresh-button:hover {
  background-color: #0056b3;
}

.table-container {
  flex: 1 1 30%;
  margin: 10px;
  min-width: 100px;
}

table {
  width: 100%;
  border-collapse: collapse;
  word-wrap: break-word;
  table-layout: fixed;
}

col.col-10 {
  width: 10%;
}

col.col-40 {
  width: 40%;
}

th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
}

th {
  white-space: normal;
  text-wrap: balance;
}

td {
  white-space: nowrap;
}

th:hover, td:hover {
  background-color: #f9f9f9; /* Optional: highlight cell on hover */
  overflow: visible; /* Prevent clipping */
  text-wrap: auto; /* Allow text wrapping */
}

.tooltip::after {
  content: attr(data-fulltext); /* Use the full text for the tooltip */
  position: absolute;
  background-color: #333; /* Tooltip background */
  color: #fff; /* Tooltip text color */
  padding: 4px 8px;
  border-radius: 4px;
  white-space: pre-line; /* Support line breaks in hydrated worker tooltip */
  font-size: 12px;
  display: none; /* Hidden by default */
  z-index: 10;
  top: 100%; /* Position below the cell */
  left: 50%;
  transform: translateX(-50%);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  overflow: visible;
}

.tooltip:hover::after {
  display: block; /* Show tooltip on hover */
  overflow: visible; /* Prevent clipping */
}

.details-container {
  cursor: pointer;
  position: relative;
  padding-bottom: 16px; /* Space for the indicator */
}

.details-container:hover {
  background-color: #f0f0f0;
}

.details-json {
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
  max-height: 200px;
  overflow-y: auto;
  background-color: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
  margin: 0;
}

.expand-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: #666;
  background-color: rgba(240, 240, 240, 0.8);
  padding: 2px 8px;
  border-radius: 4px;
  border-top: 1px solid #ddd;
}

.truncated .details-json {
  position: relative;
}

.admin-panel {
  padding: 20px;
}

.admin-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.danger-button {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.danger-button:hover {
  background-color: #c82333;
}

.admin-message {
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 4px;
}

.test-worker-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.test-worker-actions h3 {
  margin-bottom: 10px;
}

.test-button {
  background-color: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
}

.test-button:hover {
  background-color: #218838;
}

.test-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.toggle-details-button {
  display: none; /* Hide the button since we're not using it anymore */
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.pagination-info {
  font-size: 12px;
  color: #666;
}

.pagination-actions {
  display: flex;
  gap: 10px;
}

.page-info {
  font-size: 12px;
  color: #666;
}

.sort-button {
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}

.sort-indicator {
  font-size: 11px;
  color: #6b7280;
}

.worker-primary {
  font-weight: 600;
}

.worker-secondary {
  color: #6b7280;
  font-size: 12px;
}

.worker-user {
  font-weight: 600;
  color: #374151;
}

.worker-version,
.worker-capabilities {
  white-space: normal;
  word-break: break-word;
}

.capability-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.capability-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #1e3a8a;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
}

.assignment-link {
  margin-top: 4px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #0f766e;
  font-size: 12px;
  text-decoration: underline;
}

.assignment-link:hover {
  color: #115e59;
}

.actions-cell {
  min-width: 92px;
}

.worker-delete-button {
  width: auto;
  min-width: 72px;
}

.worker-value {
  font-weight: 600;
}

.raw-id-link {
  color: #2563eb;
  text-decoration: underline;
}

.raw-id-link:hover {
  color: #1d4ed8;
}

.heartbeat-fresh {
  color: #16a34a;
}

.heartbeat-warning {
  color: #ea580c;
}

.heartbeat-stale {
  color: #dc2626;
}

:deep(.action-delete) {
  white-space: nowrap;
  justify-content: center;
}
</style>
